import { useEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { CreativeEditorBackTarget } from '@/rve/editorBackNav';

const BACK_SLOT_ATTR = 'data-aspectly-neo-back-slot';
const SIDEBAR_TRIGGER_SELECTOR = '[data-sidebar="trigger"]';

interface NeoEditorTopBarBackProps {
  containerRef: RefObject<HTMLElement | null>;
  backTo: CreativeEditorBackTarget;
  onBack: () => void;
}

/** Inserts a back control immediately after the RVE sidebar toggle in the top header. */
export function NeoEditorTopBarBack({ containerRef, backTo, onBack }: NeoEditorTopBarBackProps) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    let mountedSlot: HTMLDivElement | null = null;

    const mountSlot = () => {
      if (mountedSlot?.isConnected) return true;
      const trigger = root.querySelector<HTMLElement>(SIDEBAR_TRIGGER_SELECTOR);
      const header = trigger?.closest('header') ?? root.querySelector('header');
      if (!trigger && !header) return false;

      const next = document.createElement('div');
      next.setAttribute(BACK_SLOT_ATTR, '');
      next.className = 'rve:flex rve:items-center rve:gap-1.5 rve:shrink-0';
      if (trigger) {
        trigger.insertAdjacentElement('afterend', next);
      } else {
        header!.insertAdjacentElement('afterbegin', next);
      }
      mountedSlot = next;
      setSlot(next);
      return true;
    };

    if (mountSlot()) {
      return () => {
        mountedSlot?.remove();
        setSlot(null);
      };
    }

    const observer = new MutationObserver(() => {
      if (mountSlot()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mountedSlot?.remove();
      setSlot(null);
    };
  }, [containerRef]);

  if (!slot) return null;

  return createPortal(
    <button
      type="button"
      onClick={onBack}
      title={`Back to ${backTo.label}`}
      className="rve:inline-flex rve:h-7 rve:items-center rve:gap-1.5 rve:rounded-md rve:px-2 rve:text-sm rve:font-medium rve:text-foreground rve:transition-colors rve:hover:bg-accent"
    >
      <span className="material-symbols-outlined rve:text-[18px]">arrow_back</span>
      <span className="rve:hidden rve:md:inline">{backTo.label}</span>
    </button>,
    slot,
  );
}
